import { useState, useEffect } from "react";
import { HeroShared } from "../../../../api/repositories/cdn/models/shared";
import { HeroesRepository } from "../../../../api/repositories/stats/heroes";
import { HeroItemBuild, TalentItemBuild, GameStage, ItemBuild } from "../../../../api/repositories/stats/models/item-builds";
import { localize } from "../../../../common/services/localization";
import { HeroTemplateBlock, HeroTemplateHeaderClass, SubOutdated } from "../../layout/blocks";
import { TalentSharedImage, ItemBuildImage, ItemBuildsUnsubImage } from "../../ui/file-contents";
import { SubscribeGlobalState } from "../../../../common/hooks/global-state";

export const ItemBuildsBlock: React.FC<{
    hero: Hero
    heroDetails: HeroShared
}> = ({ hero, heroDetails }) => {
    const authorized = SubscribeGlobalState("authorizedUser")

    const isHasSub = authorized && authorized.authorized && authorized.dotaPlusExpireDate >= Date.now();
    const heroName = hero.name;
    const talents = [] as { name: string, imagePath: string }[];

    hero.talents.forEach((talent, i) => talents.push({
        name: talent,
        imagePath: heroDetails.orangeTalents[`skill${i + 1}`][0].image
    }));

    Object.keys(heroDetails.orangeTalents);

    const [activeTalent, setActiveTalent] = useState<string>(talents[0].name);
    const [response, setResponse] = useState<ApiDataResponse<HeroItemBuild | undefined>>();

    useEffect(() => {
        const repository = new HeroesRepository()

        if (isHasSub) {
            repository.getItemBuild(heroName)
                .then(t => setResponse(t));
        }
    }, [hero.name])

    return <HeroTemplateBlock
        className={`${isHasSub ? '' : 'p-0'}`}
        child={
            isHasSub
                ? <div>
                    <div className="flex flex-col xl:flex-row items-center gap-[18px] justify-between mb-[32px]">
                        <span className={`${HeroTemplateHeaderClass}`}>
                            {localize('hero_builds_title')}
                        </span>
                        <div className="flex flex-row items-center gap-[8px]">
                            {
                                talents.map(talent => <div
                                    key={talent.name}
                                    onClick={() => setActiveTalent(talent.name)}
                                >
                                    <TalentSharedImage
                                        className={`w-[56px] h-[56px] cursor-pointer ${activeTalent == talent.name
                                            ? 'border-orange-1 brightness-[100%]'
                                            : 'hover:brightness-[100%] brightness-[30%]'}`
                                        }
                                        heroName={heroName}
                                        path={talent.imagePath}
                                    />
                                </div>)
                            }
                        </div>
                    </div>
                    {
                        response?.data?.talentBuilds?.[activeTalent]
                            ? <TalentBuildBlock key={activeTalent} itemBuilds={response.data.talentBuilds[activeTalent]} />
                            : null
                    }
                    <div className="mt-[50px] text-[12px] w-[300px] lg:w-full lg:text-18 text-gray font-['Roboto']">
                        {localize('hero_builds_description')}
                    </div>
                </div>
                : <>
                    <div className="2xl:relative hidden 2xl:block">
                        <ItemBuildsUnsubImage className="" />
                        <SubOutdated
                            className="absolute top-1/4 font-semibold left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                            withBtn={true}
                            authorizedUser={authorized}
                        />
                    </div>
                    <SubOutdated
                        className="2xl:hidden w-[300px] lg:w-full text-center items-center mx-auto"
                        withBtn={true}
                        authorizedUser={authorized}
                    />
                </>

        }
    />
}

const TalentBuildBlock: React.FC<{
    itemBuilds: TalentItemBuild
}> = ({ itemBuilds }) => {
    return <div className="grid grid-cols-1 lg:grid-cols-4 2xl:grid-cols-4 flex item-center justify-between gap-[34px] md:gap-[14px]">
        <StagedBuildBlock stage="start" build={itemBuilds.startItems} />
        <StagedBuildBlock stage="early" build={itemBuilds.earlyItems} />
        <StagedBuildBlock stage="mid" build={itemBuilds.midItems} />
        <StagedBuildBlock stage="late" build={itemBuilds.lateItems} />
    </div>
}

export const StagedBuildBlock: React.FC<{
    stage: GameStage
    build?: ItemBuild;
}> = ({ build, stage }) => {
    const items = build?.items ?? Array(6).fill("item_empty");
    const buildPaths = [
        ...items.slice(0, 3).map(i => i.replace("item_", "")),
        build?.hasAghanimScepter ? "aghsstatus_scepter_on" : "aghsstatus_scepter_off",
        ...items.slice(3, 6).map(i => i.replace("item_", "")),
        build?.hasAghanimShard ? "aghsstatus_shard_on" : "aghsstatus_shard_off",
    ];

    return (
        <div className="flex flex-col items-center w-full justify-center">
            <div
                className="grid grid-cols-4 gap-[6px] p-[14px] place-items-center shadow-[2px_3px_6px_0px_rgba(0,0,0,0.57)]"
                style={{
                    backgroundImage: "url('/images/inventory_bg.png')",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center",
                }}
            >
                {buildPaths.map((name, i) => (
                    <ItemBuildImage key={`${name}_${i}`} name={name} />
                ))}
            </div>
            <p
                className="mt-[14px] lg:text-20 font-semibold"
                dangerouslySetInnerHTML={{ __html: localize(`dota_game_stage_${stage}`) }}
            />
        </div>
    );
};




